import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface SecurityContextEditorProps {
  flow: any
  canEdit: boolean
  onSave: (updatedManifest: any, configName: string) => void
}

export default function SecurityContextEditor({ flow, canEdit, onSave }: SecurityContextEditorProps) {
  const { t } = useTranslation(['pipeline-studio', 'common'])
  const [isEditing, setIsEditing] = useState(false)

  // Pod Security Context edit state
  const [podRunAsUser, setPodRunAsUser] = useState('')
  const [podRunAsGroup, setPodRunAsGroup] = useState('')
  const [podRunAsNonRoot, setPodRunAsNonRoot] = useState<boolean | undefined>(undefined)
  const [podFsGroup, setPodFsGroup] = useState('')
  const [podFsGroupChangePolicy, setPodFsGroupChangePolicy] = useState('')
  const [podSupplementalGroups, setPodSupplementalGroups] = useState('')

  // Container Security Context edit state
  const [containerRunAsUser, setContainerRunAsUser] = useState('')
  const [containerRunAsGroup, setContainerRunAsGroup] = useState('')
  const [containerRunAsNonRoot, setContainerRunAsNonRoot] = useState<boolean | undefined>(undefined)
  const [containerAllowPrivilegeEscalation, setContainerAllowPrivilegeEscalation] = useState<boolean | undefined>(undefined)
  const [containerPrivileged, setContainerPrivileged] = useState<boolean | undefined>(undefined)
  const [containerReadOnlyRootFilesystem, setContainerReadOnlyRootFilesystem] = useState<boolean | undefined>(undefined)
  const [containerCapabilitiesAdd, setContainerCapabilitiesAdd] = useState('')
  const [containerCapabilitiesDrop, setContainerCapabilitiesDrop] = useState('')

  const handleEdit = () => {
    // Initialize Pod Security Context
    setPodRunAsUser(flow.spec?.securityContext?.pod?.runAsUser?.toString() || '')
    setPodRunAsGroup(flow.spec?.securityContext?.pod?.runAsGroup?.toString() || '')
    setPodRunAsNonRoot(flow.spec?.securityContext?.pod?.runAsNonRoot)
    setPodFsGroup(flow.spec?.securityContext?.pod?.fsGroup?.toString() || '')
    setPodFsGroupChangePolicy(flow.spec?.securityContext?.pod?.fsGroupChangePolicy || '')
    setPodSupplementalGroups(
      flow.spec?.securityContext?.pod?.supplementalGroups && Array.isArray(flow.spec.securityContext.pod.supplementalGroups)
        ? flow.spec.securityContext.pod.supplementalGroups.join(', ')
        : ''
    )

    // Initialize Container Security Context
    setContainerRunAsUser(flow.spec?.securityContext?.container?.runAsUser?.toString() || '')
    setContainerRunAsGroup(flow.spec?.securityContext?.container?.runAsGroup?.toString() || '')
    setContainerRunAsNonRoot(flow.spec?.securityContext?.container?.runAsNonRoot)
    setContainerAllowPrivilegeEscalation(flow.spec?.securityContext?.container?.allowPrivilegeEscalation)
    setContainerPrivileged(flow.spec?.securityContext?.container?.privileged)
    setContainerReadOnlyRootFilesystem(flow.spec?.securityContext?.container?.readOnlyRootFilesystem)
    setContainerCapabilitiesAdd(
      flow.spec?.securityContext?.container?.capabilities?.add && Array.isArray(flow.spec.securityContext.container.capabilities.add)
        ? flow.spec.securityContext.container.capabilities.add.join(', ')
        : ''
    )
    setContainerCapabilitiesDrop(
      flow.spec?.securityContext?.container?.capabilities?.drop && Array.isArray(flow.spec.securityContext.container.capabilities.drop)
        ? flow.spec.securityContext.container.capabilities.drop.join(', ')
        : ''
    )

    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSave = () => {
    // Build Pod Security Context
    const podContext: any = {}
    if (podRunAsUser.trim()) {
      const uid = parseInt(podRunAsUser.trim(), 10)
      if (!isNaN(uid)) podContext.runAsUser = uid
    }
    if (podRunAsGroup.trim()) {
      const gid = parseInt(podRunAsGroup.trim(), 10)
      if (!isNaN(gid)) podContext.runAsGroup = gid
    }
    if (podRunAsNonRoot !== undefined) podContext.runAsNonRoot = podRunAsNonRoot
    if (podFsGroup.trim()) {
      const fsg = parseInt(podFsGroup.trim(), 10)
      if (!isNaN(fsg)) podContext.fsGroup = fsg
    }
    if (podFsGroupChangePolicy.trim()) podContext.fsGroupChangePolicy = podFsGroupChangePolicy.trim()
    if (podSupplementalGroups.trim()) {
      const groups = podSupplementalGroups.split(',').map(g => parseInt(g.trim(), 10)).filter(g => !isNaN(g))
      if (groups.length > 0) podContext.supplementalGroups = groups
    }

    // Build Container Security Context
    const containerContext: any = {}
    if (containerRunAsUser.trim()) {
      const uid = parseInt(containerRunAsUser.trim(), 10)
      if (!isNaN(uid)) containerContext.runAsUser = uid
    }
    if (containerRunAsGroup.trim()) {
      const gid = parseInt(containerRunAsGroup.trim(), 10)
      if (!isNaN(gid)) containerContext.runAsGroup = gid
    }
    if (containerRunAsNonRoot !== undefined) containerContext.runAsNonRoot = containerRunAsNonRoot
    if (containerAllowPrivilegeEscalation !== undefined) containerContext.allowPrivilegeEscalation = containerAllowPrivilegeEscalation
    if (containerPrivileged !== undefined) containerContext.privileged = containerPrivileged
    if (containerReadOnlyRootFilesystem !== undefined) containerContext.readOnlyRootFilesystem = containerReadOnlyRootFilesystem

    // Build capabilities
    const capabilities: any = {}
    if (containerCapabilitiesAdd.trim()) {
      const caps = containerCapabilitiesAdd.split(',').map(c => c.trim()).filter(c => c.length > 0)
      if (caps.length > 0) capabilities.add = caps
    }
    if (containerCapabilitiesDrop.trim()) {
      const caps = containerCapabilitiesDrop.split(',').map(c => c.trim()).filter(c => c.length > 0)
      if (caps.length > 0) capabilities.drop = caps
    }
    if (Object.keys(capabilities).length > 0) containerContext.capabilities = capabilities

    // Build security context
    const securityContext: any = {}
    if (Object.keys(podContext).length > 0) securityContext.pod = podContext
    if (Object.keys(containerContext).length > 0) securityContext.container = containerContext

    // Create updated flow object
    const updatedFlow = {
      ...flow,
      spec: {
        ...flow.spec,
        securityContext: Object.keys(securityContext).length > 0 ? securityContext : undefined,
      }
    }

    // Remove undefined fields
    if (!updatedFlow.spec.securityContext) {
      delete updatedFlow.spec.securityContext
    }

    onSave(updatedFlow, t('detail.securityContextConfig'))
    setIsEditing(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-text-dark">{t('detail.securityContextConfig', 'Security Context')}</h2>
        {canEdit && !isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('detail.edit', 'Edit')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div>
          {/* Pod Security Context */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
              {t('detail.podSecurityContext', 'Pod Security Context')}
            </h3>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.runAsUser', 'Run As User')}</dt>
                <input
                  type="number"
                  value={podRunAsUser}
                  onChange={(e) => setPodRunAsUser(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                  placeholder="1000"
                />
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.runAsGroup', 'Run As Group')}</dt>
                <input
                  type="number"
                  value={podRunAsGroup}
                  onChange={(e) => setPodRunAsGroup(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                  placeholder="3000"
                />
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.runAsNonRoot', 'Run As Non-Root')}</dt>
                <select
                  value={podRunAsNonRoot === undefined ? '' : podRunAsNonRoot.toString()}
                  onChange={(e) => setPodRunAsNonRoot(e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                >
                  <option value="">-- Not Set --</option>
                  <option value="true">{t('common.yes', 'Yes')}</option>
                  <option value="false">{t('common.no', 'No')}</option>
                </select>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.fsGroup', 'FS Group')}</dt>
                <input
                  type="number"
                  value={podFsGroup}
                  onChange={(e) => setPodFsGroup(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                  placeholder="2000"
                />
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.fsGroupChangePolicy', 'FS Group Change Policy')}</dt>
                <select
                  value={podFsGroupChangePolicy}
                  onChange={(e) => setPodFsGroupChangePolicy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                >
                  <option value="">-- Select --</option>
                  <option value="OnRootMismatch">OnRootMismatch</option>
                  <option value="Always">Always</option>
                </select>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">
                  {t('detail.supplementalGroups', 'Supplemental Groups')}
                  <span className="text-xs text-gray-400 ml-2">(comma-separated)</span>
                </dt>
                <input
                  type="text"
                  value={podSupplementalGroups}
                  onChange={(e) => setPodSupplementalGroups(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                  placeholder="1000, 2000"
                />
              </div>
            </dl>
          </div>

          {/* Container Security Context */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
              {t('detail.containerSecurityContext', 'Container Security Context')}
            </h3>
            <dl className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.runAsUser', 'Run As User')}</dt>
                <input
                  type="number"
                  value={containerRunAsUser}
                  onChange={(e) => setContainerRunAsUser(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                  placeholder="1000"
                />
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.runAsGroup', 'Run As Group')}</dt>
                <input
                  type="number"
                  value={containerRunAsGroup}
                  onChange={(e) => setContainerRunAsGroup(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                  placeholder="3000"
                />
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.runAsNonRoot', 'Run As Non-Root')}</dt>
                <select
                  value={containerRunAsNonRoot === undefined ? '' : containerRunAsNonRoot.toString()}
                  onChange={(e) => setContainerRunAsNonRoot(e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                >
                  <option value="">-- Not Set --</option>
                  <option value="true">{t('common.yes', 'Yes')}</option>
                  <option value="false">{t('common.no', 'No')}</option>
                </select>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.allowPrivilegeEscalation', 'Allow Privilege Escalation')}</dt>
                <select
                  value={containerAllowPrivilegeEscalation === undefined ? '' : containerAllowPrivilegeEscalation.toString()}
                  onChange={(e) => setContainerAllowPrivilegeEscalation(e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                >
                  <option value="">-- Not Set --</option>
                  <option value="true">{t('common.yes', 'Yes')}</option>
                  <option value="false">{t('common.no', 'No')}</option>
                </select>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.privileged', 'Privileged')}</dt>
                <select
                  value={containerPrivileged === undefined ? '' : containerPrivileged.toString()}
                  onChange={(e) => setContainerPrivileged(e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                >
                  <option value="">-- Not Set --</option>
                  <option value="true">{t('common.yes', 'Yes')}</option>
                  <option value="false">{t('common.no', 'No')}</option>
                </select>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-2">{t('detail.readOnlyRootFilesystem', 'Read-Only Root Filesystem')}</dt>
                <select
                  value={containerReadOnlyRootFilesystem === undefined ? '' : containerReadOnlyRootFilesystem.toString()}
                  onChange={(e) => setContainerReadOnlyRootFilesystem(e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                >
                  <option value="">-- Not Set --</option>
                  <option value="true">{t('common.yes', 'Yes')}</option>
                  <option value="false">{t('common.no', 'No')}</option>
                </select>
              </div>
            </dl>

            {/* Capabilities */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h4 className="text-sm font-medium text-gray-700 mb-3">{t('detail.capabilities', 'Capabilities')}</h4>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-2">
                    {t('detail.capabilitiesAdd', 'Add')}
                    <span className="text-xs text-gray-400 ml-2">(comma-separated)</span>
                  </dt>
                  <input
                    type="text"
                    value={containerCapabilitiesAdd}
                    onChange={(e) => setContainerCapabilitiesAdd(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                    placeholder="NET_ADMIN, SYS_TIME"
                  />
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-2">
                    {t('detail.capabilitiesDrop', 'Drop')}
                    <span className="text-xs text-gray-400 ml-2">(comma-separated)</span>
                  </dt>
                  <input
                    type="text"
                    value={containerCapabilitiesDrop}
                    onChange={(e) => setContainerCapabilitiesDrop(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm font-mono"
                    placeholder="ALL"
                  />
                </div>
              </dl>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-6">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('detail.save', 'Save')}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Pod Security Context */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
              {t('detail.podSecurityContext', 'Pod Security Context')}
            </h3>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.runAsUser', 'Run As User')}</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">
                  {flow.spec?.securityContext?.pod?.runAsUser !== undefined
                    ? flow.spec.securityContext.pod.runAsUser
                    : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.runAsGroup', 'Run As Group')}</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">
                  {flow.spec?.securityContext?.pod?.runAsGroup !== undefined
                    ? flow.spec.securityContext.pod.runAsGroup
                    : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.runAsNonRoot', 'Run As Non-Root')}</dt>
                <dd className="mt-1">
                  {flow.spec?.securityContext?.pod?.runAsNonRoot !== undefined ? (
                    flow.spec.securityContext.pod.runAsNonRoot ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('common.yes', 'Yes')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {t('common.no', 'No')}
                      </span>
                    )
                  ) : (
                    <span className="text-sm text-gray-900">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.fsGroup', 'FS Group')}</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">
                  {flow.spec?.securityContext?.pod?.fsGroup !== undefined
                    ? flow.spec.securityContext.pod.fsGroup
                    : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.fsGroupChangePolicy', 'FS Group Change Policy')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {flow.spec?.securityContext?.pod?.fsGroupChangePolicy || '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.supplementalGroups', 'Supplemental Groups')}</dt>
                <dd className="mt-1">
                  {flow.spec?.securityContext?.pod?.supplementalGroups &&
                   Array.isArray(flow.spec.securityContext.pod.supplementalGroups) &&
                   flow.spec.securityContext.pod.supplementalGroups.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {flow.spec.securityContext.pod.supplementalGroups.map((group: number, index: number) => (
                        <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-800">
                          {group}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-900">-</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Container Security Context */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
              {t('detail.containerSecurityContext', 'Container Security Context')}
            </h3>
            <dl className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.runAsUser', 'Run As User')}</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">
                  {flow.spec?.securityContext?.container?.runAsUser !== undefined
                    ? flow.spec.securityContext.container.runAsUser
                    : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.runAsGroup', 'Run As Group')}</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">
                  {flow.spec?.securityContext?.container?.runAsGroup !== undefined
                    ? flow.spec.securityContext.container.runAsGroup
                    : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.runAsNonRoot', 'Run As Non-Root')}</dt>
                <dd className="mt-1">
                  {flow.spec?.securityContext?.container?.runAsNonRoot !== undefined ? (
                    flow.spec.securityContext.container.runAsNonRoot ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('common.yes', 'Yes')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {t('common.no', 'No')}
                      </span>
                    )
                  ) : (
                    <span className="text-sm text-gray-900">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.allowPrivilegeEscalation', 'Allow Privilege Escalation')}</dt>
                <dd className="mt-1">
                  {flow.spec?.securityContext?.container?.allowPrivilegeEscalation !== undefined ? (
                    flow.spec.securityContext.container.allowPrivilegeEscalation ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {t('common.yes', 'Yes')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('common.no', 'No')}
                      </span>
                    )
                  ) : (
                    <span className="text-sm text-gray-900">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.privileged', 'Privileged')}</dt>
                <dd className="mt-1">
                  {flow.spec?.securityContext?.container?.privileged !== undefined ? (
                    flow.spec.securityContext.container.privileged ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {t('common.yes', 'Yes')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('common.no', 'No')}
                      </span>
                    )
                  ) : (
                    <span className="text-sm text-gray-900">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.readOnlyRootFilesystem', 'Read-Only Root Filesystem')}</dt>
                <dd className="mt-1">
                  {flow.spec?.securityContext?.container?.readOnlyRootFilesystem !== undefined ? (
                    flow.spec.securityContext.container.readOnlyRootFilesystem ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('common.yes', 'Yes')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {t('common.no', 'No')}
                      </span>
                    )
                  ) : (
                    <span className="text-sm text-gray-900">-</span>
                  )}
                </dd>
              </div>
            </dl>

            {/* Capabilities */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h4 className="text-sm font-medium text-gray-700 mb-3">{t('detail.capabilities', 'Capabilities')}</h4>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('detail.capabilitiesAdd', 'Add')}</dt>
                  <dd className="mt-1">
                    {flow.spec?.securityContext?.container?.capabilities?.add &&
                     Array.isArray(flow.spec.securityContext.container.capabilities.add) &&
                     flow.spec.securityContext.container.capabilities.add.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {flow.spec.securityContext.container.capabilities.add.map((cap: string, index: number) => (
                          <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-blue-100 text-blue-800">
                            {cap}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-900">-</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('detail.capabilitiesDrop', 'Drop')}</dt>
                  <dd className="mt-1">
                    {flow.spec?.securityContext?.container?.capabilities?.drop &&
                     Array.isArray(flow.spec.securityContext.container.capabilities.drop) &&
                     flow.spec.securityContext.container.capabilities.drop.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {flow.spec.securityContext.container.capabilities.drop.map((cap: string, index: number) => (
                          <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-800">
                            {cap}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-900">-</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
